import { useState, type FormEvent } from "react";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { blogArticles } from "@/mocks/blogData";

const NEWSLETTER_FORM_URL = "https://readdy.ai/api/form/da39in90938upkd4h6v0";

interface BlogDetailPageProps {
  slug: string;
}

export default function BlogDetailPage({ slug }: BlogDetailPageProps) {
  const article = blogArticles.find((a) => a.slug === slug);

  const [email, setEmail] = useState("");
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  if (!article) return null;

  const related = blogArticles.filter((a) => a.slug !== slug).slice(0, 3);

  const toc = article.content.filter((b) => b.type === "h2" || b.type === "h3");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = data.get("contact_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormState("success");
      form.reset();
      return;
    }
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "contact_alt" && value) payload.append(key, String(value));
    });
    setFormState("submitting");
    setFormError("");
    try {
      const res = await fetch(NEWSLETTER_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      if (!res.ok || parsed?.code !== "OK" || isSpam) {
        setFormState("error");
        setFormError(serverMsg && String(serverMsg) !== "" ? String(serverMsg) : "Something went wrong. Please try again.");
        return;
      }
      setFormState("success");
      form.reset();
    } catch {
      setFormState("error");
      setFormError("Something went wrong. Please try again.");
    }
  };

  return (
    <PageLayout>
      <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Blog", href: "/blog" },
            { label: article.category },
          ]}
        />

        {/* Article Hero */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="container-astro max-w-4xl">
            <AnimatedSection>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="px-3 py-1 rounded-full bg-accent-500/10 text-accent-600 text-xs font-bold border border-accent-200/50">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                  <i className="ri-time-line" />
                  {article.readingTime}
                </span>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-6 font-heading tracking-tight leading-tight">
                {article.title}
              </h1>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white text-sm font-bold">
                    AE
                  </span>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground-900">{article.author}</span>
                    <span className="text-xs text-foreground-500">{article.authorRole}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span>{article.date}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300" />
                  <span>{article.readingTime}</span>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={300}>
              <div className="rounded-2xl overflow-hidden border border-background-200/60">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-[300px] md:h-[440px] object-cover object-top"
                />
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Body + Sidebar */}
        <section className="pb-16 md:pb-24 bg-background-100">
          <div className="container-astro">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
              {/* TOC */}
              <div className="lg:col-span-3 order-2 lg:order-1">
                <div className="lg:sticky lg:top-28 rounded-2xl bg-background-50 border border-background-200/60 p-6">
                  <h4 className="text-sm font-bold text-foreground-950 mb-4 font-heading flex items-center gap-2">
                    <i className="ri-list-unordered text-accent-600" />
                    Table of Contents
                  </h4>
                  <ul className="flex flex-col gap-2.5">
                    {toc.map((b) => (
                      <li key={b.text}>
                        <a
                          href={`#${(b.text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`text-sm transition-colors hover:text-accent-600 ${
                            b.type === "h3" ? "pl-4 text-foreground-500" : "text-foreground-700 font-medium"
                          }`}
                        >
                          {b.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-background-200/50">
                    <p className="text-sm font-semibold text-foreground-900 mb-3">Share this article</p>
                    <div className="flex items-center gap-2">
                      {[
                        { icon: "ri-linkedin-fill", label: "LinkedIn" },
                        { icon: "ri-twitter-x-line", label: "X" },
                        { icon: "ri-facebook-fill", label: "Facebook" },
                        { icon: "ri-link", label: "Copy link" },
                      ].map((s) => (
                        <button
                          key={s.label}
                          onClick={() => {
                            if (s.label === "Copy link") navigator.clipboard?.writeText(window.location.href);
                          }}
                          className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-300"
                          aria-label={s.label}
                        >
                          <i className={s.icon} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="lg:col-span-9 order-1 lg:order-2">
                <article className="prose-article max-w-3xl">
                  {article.content.map((block, i) => {
                    if (block.type === "h2") {
                      const id = (block.text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
                      return (
                        <h2
                          key={i}
                          id={id}
                          className="text-2xl md:text-[28px] font-bold text-foreground-950 mt-10 mb-4 font-heading tracking-tight scroll-mt-28"
                        >
                          {block.text}
                        </h2>
                      );
                    }
                    if (block.type === "h3") {
                      const id = (block.text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
                      return (
                        <h3
                          key={i}
                          id={id}
                          className="text-lg md:text-xl font-bold text-foreground-950 mt-8 mb-3 font-heading scroll-mt-28"
                        >
                          {block.text}
                        </h3>
                      );
                    }
                    if (block.type === "paragraph") {
                      return (
                        <p key={i} className="text-base md:text-[17px] text-foreground-700 leading-relaxed mb-6">
                          {block.text}
                        </p>
                      );
                    }
                    if (block.type === "code") {
                      return (
                        <div key={i} className="rounded-xl bg-primary-950 text-background-100 text-sm leading-relaxed font-mono p-5 md:p-6 my-6 overflow-x-auto border border-primary-800/40">
                          <pre className="whitespace-pre">{block.code}</pre>
                        </div>
                      );
                    }
                    if (block.type === "callout") {
                      return (
                        <div key={i} className="flex items-start gap-3 rounded-xl bg-accent-50 border border-accent-200/50 p-5 md:p-6 my-6">
                          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 shrink-0">
                            <i className="ri-lightbulb-flash-line" />
                          </span>
                          <p className="text-sm md:text-base text-foreground-800 leading-relaxed font-medium">
                            {block.text}
                          </p>
                        </div>
                      );
                    }
                    if (block.type === "list") {
                      return (
                        <ul key={i} className="space-y-2.5 my-6">
                          {(block.items || []).map((item) => (
                            <li key={item} className="flex items-start gap-3">
                              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-600 shrink-0 mt-0.5">
                                <i className="ri-check-line text-xs" />
                              </span>
                              <span className="text-base text-foreground-700 leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return null;
                  })}
                </article>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="container-astro">
            <AnimatedSection>
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                  Related Articles
                </h2>
                <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
              </div>
            </AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((r, i) => (
                <AnimatedSection key={r.slug} delay={i * 100}>
                  <a
                    href={`/blog/${r.slug}`}
                    className="group flex flex-col rounded-2xl overflow-hidden bg-background-100 border border-background-200/60 hover:border-accent-300/40 hover:shadow-xl hover:shadow-accent-500/10 transition-all duration-500 card-lift h-full"
                  >
                    <div className="relative overflow-hidden h-[160px]">
                      <img src={r.image} alt={r.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                      <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full bg-background-50/90 text-xs font-bold text-accent-600">
                        {r.category}
                      </span>
                    </div>
                    <div className="p-5 flex flex-col flex-grow">
                      <h3 className="text-sm font-bold text-foreground-950 mb-2 font-heading leading-snug group-hover:text-accent-600 transition-colors">
                        {r.title}
                      </h3>
                      <span className="text-xs text-foreground-500 mb-3 flex-grow">{r.date}</span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600">
                        Read Article
                        <i className="ri-arrow-right-line text-xs" />
                      </span>
                    </div>
                  </a>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 md:py-20 bg-hero-gradient relative overflow-hidden">
          <div className="container-astro relative z-10 max-w-3xl text-center">
            <AnimatedSection>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-background-50 mb-4 font-heading tracking-tight">
                Stay Updated With Technology Insights
              </h2>
              <p className="text-base text-background-400 mb-8 leading-relaxed">
                Get practical articles on Laravel, SaaS, AI and cloud engineering delivered to your inbox.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              {formState === "success" ? (
                <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm font-semibold">
                  <i className="ri-checkbox-circle-fill text-lg" />
                  Thanks for subscribing! We'll be in touch.
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
                >
                  <input
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-5 py-3.5 rounded-xl bg-background-50/10 border border-background-500/30 text-background-100 text-sm placeholder:text-background-500 focus:outline-none focus:ring-2 focus:ring-accent-500/50 focus:border-accent-500/40 transition-all"
                  />
                  <input
                    type="text"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-trap"
                  />
                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap"
                  >
                    {formState === "submitting" ? "Subscribing..." : "Subscribe"}
                    <i className="ri-send-plane-line text-xs" />
                  </button>
                </form>
              )}
              {formState === "error" && (
                <p className="mt-4 text-sm text-rose-300">{formError}</p>
              )}
            </AnimatedSection>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
        </section>
    </PageLayout>
  );
}